import React,{useState,useRef,useCallback,useEffect} from 'react';
import {Pitch} from '../components/Match';
import {SUPA_URL,api} from '../lib/api';
import {BADGE_ICON_OPTIONS,DEVELOPMENT_SUGGESTIONS,FEATURE_TOGGLES,FLAGS,HOME_AWAY_STATS,STRENGTH_SUGGESTIONS,TERMS_LAST_UPDATED,TERMS_SECTIONS,TERMS_VERSION,achInfo} from '../lib/constants';
import {getNotificationPrefs,subscribeToPush,unsubscribeFromPush,upsertNotificationPref} from '../lib/pushNotifications';
import {flagCodeFromEmoji,fmtDateShort,fmtMins,getA11yPrefs,homeAwaySplit,isFeatureOn,setA11yPref,today} from '../lib/utils';

export function PitchBackdrop({color1,color2,brightness,matchHalves,stripes}={}){
  const c1=color1||"#cfe6b0";
  // matchHalves: use Colour 1 for the whole pitch instead of blending into Colour 2.
  const c2=matchHalves?c1:(color2||"#eaf5db");
  const b=brightness==null?100:brightness;
  // stripes defaults to on (existing look) unless explicitly turned off.
  const showStripes=stripes!==false;
  return(
    <div style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none",filter:`brightness(${b}%)`}}>
      <div style={{position:"absolute",inset:0,background:`linear-gradient(90deg, ${c1} 0%, ${c2} 100%)`}}>
        {showStripes&&<div style={{position:"absolute",inset:0,background:"repeating-linear-gradient(90deg, transparent 0px, transparent 40px, rgba(255,255,255,0.25) 40px, rgba(255,255,255,0.25) 80px)"}}/>}
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}} viewBox="0 0 100 100" preserveAspectRatio="none">
          <rect x="2" y="4" width="96" height="92" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.4"/>
          <line x1="50" y1="4" x2="50" y2="96" stroke="rgba(255,255,255,0.6)" strokeWidth="0.4"/>
          <circle cx="50" cy="50" r="11" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="0.4"/>
          <circle cx="50" cy="50" r="0.6" fill="rgba(255,255,255,0.6)"/>
          <rect x="2" y="30" width="12" height="40" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4"/>
          <rect x="86" y="30" width="12" height="40" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4"/>
        </svg>
      </div>
    </div>
  );
}
// Achievement catalog — fixed in the app (not the database). "auto" ones get
// checked automatically whenever a coach saves a match result; the rest are
// awarded manually by a coach. Players can earn the same one multiple times,
// shown as a "×N" multiplier wherever badges are displayed.

export function AccessibilitySettingsPanel({ac,AS}){
  const [prefs,setPrefs]=useState(getA11yPrefs);
  function update(key,value){
    setA11yPref(key,value);
    setPrefs(p=>({...p,[key]:value}));
  }
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const L={fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8,display:"block"};
  const TEXT_SIZES=[{key:"normal",label:"Normal"},{key:"large",label:"Large"},{key:"xlarge",label:"Extra Large"}];
  const CB_MODES=[
    {key:"none",label:"Off"},
    {key:"protanopia",label:"Protanopia"},
    {key:"deuteranopia",label:"Deuteranopia"},
    {key:"tritanopia",label:"Tritanopia"},
  ];
  return(
    <div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:520}}>
      <div>
        <div style={{fontWeight:700,fontSize:15,marginBottom:2}}>♿ Accessibility</div>
        <div style={{fontSize:11,color:"var(--c-muted)"}}>These settings are saved on this device/browser, so they'll apply here every time you log in.</div>
      </div>

      <div style={{...C,padding:16}}>
        <label style={L}>🔎 Text Size</label>
        <div style={{display:"flex",gap:6}}>
          {TEXT_SIZES.map(t=>(
            <button key={t.key} onClick={()=>update("textSize",t.key)} style={{flex:1,padding:"9px",borderRadius:7,border:"none",cursor:"pointer",fontSize:t.key==="xlarge"?13:t.key==="large"?12:11,fontWeight:700,background:prefs.textSize===t.key?ac:AS.bgColor,color:prefs.textSize===t.key?"#fff":"var(--c-muted)"}}>{t.label}</button>
          ))}
        </div>
        <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:8}}>Makes text and controls throughout the app bigger and easier to read.</div>
      </div>

      <div style={{...C,padding:16}}>
        <label style={L}>🔤 Dyslexia-Friendly Reading</label>
        <button onClick={()=>update("dyslexiaFont",!prefs.dyslexiaFont)} style={{display:"flex",alignItems:"center",gap:9,background:"none",border:"none",cursor:"pointer",padding:0}}>
          <span style={{width:38,height:22,borderRadius:11,background:prefs.dyslexiaFont?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
            <span style={{position:"absolute",top:2,left:prefs.dyslexiaFont?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
          </span>
          <span style={{fontSize:12,fontWeight:600,color:"var(--c-text2)"}}>{prefs.dyslexiaFont?"On":"Off"}</span>
        </button>
        <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:8}}>Switches to a clearer font with wider letter and word spacing and more line height, which many people with dyslexia find easier to read.</div>
      </div>

      <div style={{...C,padding:16}}>
        <label style={L}>🎨 Colour Vision Mode</label>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {CB_MODES.map(m=>(
            <button key={m.key} onClick={()=>update("colorblindMode",m.key)} style={{flex:"1 1 100px",padding:"9px 6px",borderRadius:7,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:prefs.colorblindMode===m.key?ac:AS.bgColor,color:prefs.colorblindMode===m.key?"#fff":"var(--c-muted)"}}>{m.label}</button>
          ))}
        </div>
        <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:8}}>Adjusts colours across the app to make similar-looking reds, greens and blues easier to tell apart. This is a general assistive adjustment rather than a precise per-colour fix — try each option to see which is clearest for you.</div>
      </div>
    </div>
  );
}

export function HomeAwayChips({count,color}){
  if(!count||count<=0)return null;
  const cap=20;
  const shown=Math.min(count,cap);
  return(
    <div style={{display:"flex",flexWrap:"wrap",gap:3,justifyContent:"inherit"}}>
      {Array.from({length:shown},(_,i)=>i+1).map(n=>(
        <span key={n} style={{width:14,height:14,borderRadius:"50%",background:color,color:"#fff",fontSize:7,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{n}</span>
      ))}
      {count>cap&&<span style={{fontSize:9,fontWeight:700,color:"var(--c-muted)",alignSelf:"center"}}>+{count-cap}</span>}
    </div>
  );
}

export function HomeAwaySplitCard({matches,AS,title}){
  const split=homeAwaySplit(matches);
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const totalKnown=split.home.played+split.away.played;
  return(
    <div>
      <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>{title||"🏠 Home vs Away"}</div>
      {totalKnown===0?(
        <div style={{...C,padding:20,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No matches with a home/away setting yet.</div>
      ):(
        <div style={{...C,padding:14}}>
          <div style={{display:"flex",alignItems:"center",marginBottom:6}}>
            <div style={{flex:1,textAlign:"right",fontSize:12,fontWeight:800}}>🏠 Home</div>
            <div style={{width:90}}/>
            <div style={{flex:1,textAlign:"left",fontSize:12,fontWeight:800}}>Away ✈️</div>
          </div>
          {HOME_AWAY_STATS.map(s=>(
            <div key={s.key} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderTop:`1px solid ${AS.bgColor}`}}>
              <div style={{flex:1,display:"flex",justifyContent:"flex-end",alignItems:"center",gap:6}}>
                <HomeAwayChips count={split.home[s.key]} color={s.color}/>
                <span style={{fontWeight:800,fontSize:13,minWidth:18,textAlign:"right",flexShrink:0}}>{split.home[s.key]}</span>
              </div>
              <div style={{width:90,flexShrink:0,textAlign:"center",fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.03em"}}>{s.label}</div>
              <div style={{flex:1,display:"flex",justifyContent:"flex-start",alignItems:"center",gap:6}}>
                <span style={{fontWeight:800,fontSize:13,minWidth:18,flexShrink:0}}>{split.away[s.key]}</span>
                <HomeAwayChips count={split.away[s.key]} color={s.color}/>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function GoogleIcon({size=16}){
  return(<svg width={size} height={size} viewBox="0 0 48 48" style={{flexShrink:0}}>
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
  </svg>);
}

// ── MatchKeyInfo ──────────────────────────────────────────────────────────────
// The details players and parents actually need before a match — when to turn up,
// when it starts, and where — used to be squashed into one small grey caption line
// ("Wed, 08 Jul 2026 · 10:30 · Home Pitch"), which is easy to misread and easy to
// miss on a phone. This renders them as separate, clearly-labelled tiles instead.
// Shared by the player and parent match detail screens and the coach's match modal
// so all three always show the same information in the same order.
export function MatchKeyInfo({match,ac="#10b981",compact=false}){
  if(!match)return null;
  const dateStr=(()=>{
    if(!match.date)return "TBC";
    const d=new Date(match.date);
    if(isNaN(d))return match.date;
    return d.toLocaleDateString(undefined,{weekday:"short",day:"numeric",month:"short",year:"numeric"});
  })();
  // Meeting time is optional — older fixtures (and coaches who don't use it) won't
  // have one, so the tile is simply left out rather than showing an empty box.
  const tiles=[
    {icon:"📅",label:"Date",value:dateStr},
    match.meeting_time?{icon:"🕒",label:"Meeting Time",value:match.meeting_time,hint:"Be there by"}:null,
    {icon:"⚽",label:"Kick Off",value:match.kickoff||"TBC"},
    {icon:"📍",label:"Location",value:match.venue||match.location||(match.venue_type==="away"?"Away":"TBC"),link:match.map_url||null},
  ].filter(Boolean);
  return(
    <div style={{display:"grid",gridTemplateColumns:`repeat(auto-fit,minmax(${compact?125:140}px,1fr))`,gap:8,margin:"10px 0 4px"}}>
      {tiles.map(t=>(
        <div key={t.label} style={{background:"var(--c-bg0)",border:"1px solid var(--c-border)",borderRadius:11,padding:compact?"9px 11px":"11px 13px",minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,letterSpacing:"0.4px",textTransform:"uppercase",color:"var(--c-muted)"}}>
            <span style={{fontSize:12}}>{t.icon}</span>{t.label}
          </div>
          <div style={{fontSize:compact?15:17,fontWeight:800,marginTop:3,lineHeight:1.25,wordBreak:"break-word"}}>{t.value}</div>
          {t.hint&&<div style={{fontSize:10,color:"var(--c-muted)",marginTop:1,fontWeight:600}}>{t.hint}</div>}
          {t.link&&<a href={t.link} target="_blank" rel="noopener noreferrer" style={{display:"inline-block",marginTop:4,fontSize:11,fontWeight:700,color:ac,textDecoration:"none"}}>View on Map →</a>}
        </div>
      ))}
    </div>
  );
}

// Decodes a flag emoji (either a simple 2-letter regional-indicator flag like 🇫🇷,
// or a complex tag-sequence subdivision flag like 🏴England) into a flagcdn.com
// country code (e.g. "fr", "gb-eng"). Returns null if the value isn't a flag emoji
// we can decode, in which case callers should fall back to rendering it as-is.

export function Flag({value,size=16}){
  const [broken,setBroken]=useState(false);
  const code=flagCodeFromEmoji(value);
  if(!code||broken) return <span>{value}</span>;
  return <img src={`https://flagcdn.com/24x18/${code}.png`} srcSet={`https://flagcdn.com/48x36/${code}.png 2x`} alt="" style={{width:size,height:Math.round(size*0.75),display:"inline-block",verticalAlign:"-2px",borderRadius:2,objectFit:"cover",background:"var(--c-border)"}} onError={()=>setBroken(true)}/>;
}

// ── NotesThread ──────────────────────────────────────────────────────────────
// Shared coach↔player messaging thread. Coaches can start a thread; players can
// only reply to one that already exists (enforced by RLS, not just the UI).

export function NotesThread({token,orgId,playerId,playerName,senderType,senderId,ac,AS,isMobile,onActivity}){
  const [thread,setThread]=useState(undefined); // undefined=loading, null=none yet
  const [messages,setMessages]=useState([]);
  const [body,setBody]=useState("");
  const [sending,setSending]=useState(false);
  const scrollRef=useRef(null);

  useEffect(()=>{ load(); },[playerId]);
  useEffect(()=>{ if(scrollRef.current)scrollRef.current.scrollTop=scrollRef.current.scrollHeight; },[messages.length]);
  useEffect(()=>{ const t=setInterval(refreshMessages,20000); return()=>clearInterval(t); },[thread?.id]);

  async function refreshMessages(){
    if(!thread)return;
    const msgs=await api.get(token,"player_note_messages",`thread_id=eq.${thread.id}&order=created_at.asc`);
    setMessages(msgs||[]);
    const otherSide=senderType==="coach"?"player":"coach";
    const hasUnread=(msgs||[]).some(m=>m.sender_type===otherSide&&!m.read_by_recipient);
    if(hasUnread){
      await api.patchWhere(token,"player_note_messages",`thread_id=eq.${thread.id}&sender_type=eq.${otherSide}&read_by_recipient=eq.false`,{read_by_recipient:true});
      if(onActivity)onActivity();
    }
  }

  async function load(){
    setThread(undefined);
    const ts=await api.get(token,"player_notes",`org_id=eq.${orgId}&player_id=eq.${playerId}`);
    if(!ts||ts.length===0){ setThread(null); setMessages([]); return; }
    const t=ts[0];
    setThread(t);
    const msgs=await api.get(token,"player_note_messages",`thread_id=eq.${t.id}&order=created_at.asc`);
    setMessages(msgs||[]);
    const otherSide=senderType==="coach"?"player":"coach";
    const hasUnread=(msgs||[]).some(m=>m.sender_type===otherSide&&!m.read_by_recipient);
    if(hasUnread){
      await api.patchWhere(token,"player_note_messages",`thread_id=eq.${t.id}&sender_type=eq.${otherSide}&read_by_recipient=eq.false`,{read_by_recipient:true});
      if(onActivity)onActivity();
    }
  }

  async function send(){
    const text=body.trim();
    if(!text||sending)return;
    setSending(true);
    let t=thread;
    if(!t){
      const created=await api.post(token,"player_notes",[{org_id:orgId,player_id:playerId,created_by:senderId}]);
      if(!created||!created[0]){setSending(false);return;}
      t=created[0];
      setThread(t);
    } else {
      await api.patch(token,"player_notes",t.id,{last_message_at:new Date().toISOString()});
    }
    const msg=await api.post(token,"player_note_messages",[{thread_id:t.id,sender_type:senderType,sender_id:senderId,body:text}]);
    if(msg&&msg[0]){setMessages(prev=>[...prev,msg[0]]);if(onActivity)onActivity();}
    setBody("");
    setSending(false);
  }

  if(thread===undefined)return <div style={{padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Loading notes…</div>;

  function dayLabel(iso){
    const d=new Date(iso),today=new Date(),yest=new Date();yest.setDate(today.getDate()-1);
    const sameDay=(a,b)=>a.toDateString()===b.toDateString();
    if(sameDay(d,today))return "Today";
    if(sameDay(d,yest))return "Yesterday";
    return d.toLocaleDateString([],{weekday:"short",month:"short",day:"numeric",year:d.getFullYear()!==today.getFullYear()?"numeric":undefined});
  }

  return(<div style={{display:"flex",flexDirection:"column",gap:10}}>
    <div ref={scrollRef} style={{maxHeight:isMobile?300:380,overflowY:"auto",display:"flex",flexDirection:"column",gap:8,padding:messages.length>0?"4px 2px":0}}>
      {messages.length===0?(
        <div style={{padding:"20px 10px",textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>{senderType==="player"?"No notes from your coach yet.":`No notes sent to ${playerName||"this player"} yet — write one below.`}</div>
      ):messages.map((m,i)=>{
        const mine=m.sender_type===senderType;
        const prev=messages[i-1];
        const showDaySep=!prev||new Date(prev.created_at).toDateString()!==new Date(m.created_at).toDateString();
        return(
          <React.Fragment key={m.id}>
            {showDaySep&&<div style={{textAlign:"center",margin:"6px 0"}}><span style={{fontSize:9,fontWeight:700,color:"var(--c-muted)",background:AS.bgColor,padding:"3px 10px",borderRadius:10}}>{dayLabel(m.created_at)}</span></div>}
            <div style={{alignSelf:mine?"flex-end":"flex-start",maxWidth:"80%",background:mine?ac:AS.bgColor,color:mine?"#fff":"var(--c-text)",borderRadius:mine?"12px 12px 3px 12px":"12px 12px 12px 3px",padding:"8px 12px"}}>
              <div style={{fontSize:13,lineHeight:1.4,whiteSpace:"pre-wrap"}}>{m.body}</div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:4,marginTop:3}}>
                <span style={{fontSize:9,opacity:0.7}}>{new Date(m.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
                {mine&&<span style={{fontSize:10,opacity:m.read_by_recipient?1:0.6}} title={m.read_by_recipient?"Read":"Sent"}>{m.read_by_recipient?"✓✓":"✓"}</span>}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
    {(senderType==="coach"||thread)&&<div style={{display:"flex",gap:7}}>
      <textarea value={body} onChange={e=>setBody(e.target.value)} placeholder={senderType==="coach"?"Write a note — technical feedback, targets, things to work on…":"Write a reply…"} rows={2} style={{flex:1,padding:"8px 10px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,resize:"none",outline:"none",fontFamily:"inherit"}}/>
      <button onClick={send} disabled={!body.trim()||sending} style={{padding:"0 16px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:body.trim()?"pointer":"default",opacity:body.trim()?1:0.5}}>{sending?"…":"Send"}</button>
    </div>}
  </div>);
}

// ── AchievementBadges ────────────────────────────────────────────────────────
// Shared display for a set of earned achievements (team or individual). Groups
// repeated awards of the same achievement into a single badge with a ×N count.
// ── Layout preference helpers ────────────────────────────────────────────────
// One generic table (user_layout_preferences) stores each user's preferred
// block order per page. `userId` is either a coach's id or a player's
// team_viewers id — both are just auth.uid(), so this works for either.

export function ReorderableBlocks({blocks,order,onOrderChange,showHandle=true}){
  const [localOrder,setLocalOrder]=useState(order);
  const [dragId,setDragId]=useState(null);
  const itemRefs=useRef({});
  const dragStateRef=useRef(null);

  useEffect(()=>{ setLocalOrder(order); },[order.join("|")]);

  function onPointerDown(e,id){
    e.preventDefault();
    dragStateRef.current={id};
    setDragId(id);
  }
  useEffect(()=>{
    if(!dragId)return;
    function onMove(e){
      const y=e.touches?e.touches[0].clientY:e.clientY;
      setLocalOrder(prev=>{
        const dragIdx=prev.indexOf(dragId);
        if(dragIdx===-1)return prev;
        for(let i=0;i<prev.length;i++){
          if(prev[i]===dragId)continue;
          const el=itemRefs.current[prev[i]];
          if(!el)continue;
          const rect=el.getBoundingClientRect();
          const mid=rect.top+rect.height/2;
          if((y<mid&&i<dragIdx)||(y>mid&&i>dragIdx)){
            const next=[...prev];
            next.splice(dragIdx,1);
            next.splice(i,0,dragId);
            return next;
          }
        }
        return prev;
      });
    }
    function onUp(){
      setDragId(null);
      setLocalOrder(finalOrder=>{ onOrderChange(finalOrder); return finalOrder; });
    }
    window.addEventListener("pointermove",onMove);
    window.addEventListener("pointerup",onUp);
    window.addEventListener("pointercancel",onUp);
    return ()=>{
      window.removeEventListener("pointermove",onMove);
      window.removeEventListener("pointerup",onUp);
      window.removeEventListener("pointercancel",onUp);
    };
  },[dragId]);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {localOrder.map(id=>{
        const block=blocks.find(b=>b.id===id);
        if(!block)return null;
        return(
          <div key={id} ref={el=>itemRefs.current[id]=el} style={{display:"flex",gap:6,alignItems:"stretch",opacity:dragId===id?0.55:1}}>
            {showHandle&&<div onPointerDown={e=>onPointerDown(e,id)} title="Drag to reorder" style={{cursor:"grab",touchAction:"none",display:"flex",alignItems:"center",padding:"0 4px",color:"var(--c-muted2)",fontSize:16,flexShrink:0,userSelect:"none"}}>⠿</div>}
            <div style={{flex:1,minWidth:0}}>{block.node}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── PlayerAvatar ─────────────────────────────────────────────────────────────
// Shows a player's photo if they have one, otherwise the usual colored circle
// with their initial — used everywhere a player might now have a photo.

export function PlayerAvatar({url,name,size,ac}){
  const s=size||40;
  if(url)return <img src={url} alt={name} style={{width:s,height:s,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>;
  return(
    <div style={{width:s,height:s,borderRadius:"50%",background:ac+"33",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:s*0.4,color:ac,flexShrink:0}}>
      {(name||"?").charAt(0).toUpperCase()}
    </div>
  );
}

// ── PlayerPhotoUploader ──────────────────────────────────────────────────────
// Wraps PlayerAvatar with an upload control (coach or the player themselves).
// Uploads to Supabase Storage at "{playerId}/photo.<ext>" (upsert), then saves
// the resulting (cache-busted) URL onto players.photo_url.

export function NotificationSettingsPanel({token,userId,ac,AS}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [subscribed,setSubscribed]=useState(null); // null=checking, true/false once known
  const [prefs,setPrefs]=useState(null);
  const [busy,setBusy]=useState(false);

  useEffect(()=>{ checkSubscribed(); loadPrefs(); },[]);
  async function checkSubscribed(){
    try{
      if(!("serviceWorker" in navigator)){setSubscribed(false);return;}
      const reg=await navigator.serviceWorker.getRegistration();
      const sub=await reg?.pushManager.getSubscription();
      setSubscribed(!!sub);
    }catch(e){ setSubscribed(false); }
  }
  async function loadPrefs(){ setPrefs(await getNotificationPrefs(token,userId)); }
  async function enable(){
    setBusy(true);
    const ok=await subscribeToPush(token,userId);
    setSubscribed(ok);
    setBusy(false);
  }
  async function disable(){
    setBusy(true);
    await unsubscribeFromPush(token);
    setSubscribed(false);
    setBusy(false);
  }
  async function toggle(field){
    const next={...prefs,[field]:!prefs[field]};
    setPrefs(next);
    await upsertNotificationPref(token,userId,field,next[field]);
  }

  const CATEGORIES=[["new_match","📅 New Match","When a new fixture is added to your team's schedule"],["match_results","📊 Match Results","When a match is marked as played with a final score"],["new_message","💬 New Message","Team chat, league chat, and direct messages"],["new_achievement","🏅 New Achievement","When you (or your child) receive an achievement"]];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16,maxWidth:520}}>
      <div style={{...C,padding:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:14}}>
        <div>
          <div style={{fontSize:13,fontWeight:700}}>🔔 Push Notifications</div>
          <div style={{fontSize:11,color:"var(--c-muted)",marginTop:3}}>{subscribed?"Enabled on this device.":"Get notified even when the app isn't open."}</div>
        </div>
        {subscribed===null?(
          <span style={{fontSize:11,color:"var(--c-muted2)"}}>Checking…</span>
        ):subscribed?(
          <button onClick={disable} disabled={busy} style={{padding:"7px 14px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontWeight:700,fontSize:11,cursor:"pointer"}}>{busy?"…":"Disable"}</button>
        ):(
          <button onClick={enable} disabled={busy} style={{padding:"7px 14px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>{busy?"Enabling…":"Enable"}</button>
        )}
      </div>
      {prefs&&(
        <div style={{...C,padding:16}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:12}}>Notify me about</div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {CATEGORIES.map(([field,label,desc])=>(
              <div key={field} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:14}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600}}>{label}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{desc}</div>
                </div>
                <button onClick={()=>toggle(field)} style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background:prefs[field]?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
                  <span style={{position:"absolute",top:2,left:prefs[field]?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {prefs&&(
        <>
          <div style={{...C,padding:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:14}}>
            <div>
              <div style={{fontSize:13,fontWeight:700}}>📧 Email Notifications</div>
              <div style={{fontSize:11,color:"var(--c-muted)",marginTop:3}}>{prefs.email_enabled?"Enabled — sent to your account email.":"Get emailed about the same events, even when you're not in the app."}</div>
            </div>
            <button onClick={()=>toggle("email_enabled")} style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background:prefs.email_enabled?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
              <span style={{position:"absolute",top:2,left:prefs.email_enabled?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
            </button>
          </div>
          {prefs.email_enabled&&(
            <div style={{...C,padding:16}}>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:12}}>Email me about</div>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {CATEGORIES.map(([field,label,desc])=>{
                  const emailField=`email_${field}`;
                  return(
                    <div key={emailField} style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:14}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:600}}>{label}</div>
                        <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{desc}</div>
                      </div>
                      <button onClick={()=>toggle(emailField)} style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background:prefs[emailField]?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
                        <span style={{position:"absolute",top:2,left:prefs[emailField]?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function IconPickerSelect({value,onChange,AS}){
  return(
    <select value={value||"⭐"} onChange={e=>onChange(e.target.value)} style={{padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:16,cursor:"pointer"}}>
      {BADGE_ICON_OPTIONS.map(i=><option key={i} value={i}>{i}</option>)}
    </select>
  );
}

export function OrgLogoUploader({orgId,logoUrl,ac,token,onUploaded}){
  const [uploading,setUploading]=useState(false);
  const inputRef=useRef(null);
  async function onFileChange(e){
    const file=e.target.files?.[0];
    if(!file)return;
    if(!file.type.startsWith("image/")){alert("Please choose an image file.");return;}
    if(file.size>5*1024*1024){alert("Please choose an image under 5MB.");return;}
    setUploading(true);
    const ext=(file.name.split(".").pop()||"png").toLowerCase();
    const path=`${orgId}/logo.${ext}`;
    const result=await api.uploadFile(token,"org-logos",path,file);
    if(result.ok){
      const url=`${SUPA_URL}/storage/v1/object/public/org-logos/${path}?t=${Date.now()}`;
      const res=await api.patch(token,"organisations",orgId,{logo_url:url});
      if(res&&res.length>0&&onUploaded)onUploaded(url);
      else { console.error("Logo uploaded but saving logo_url to the org record failed."); alert("Logo uploaded, but couldn't save it — please try again."); }
    } else {
      console.error("Logo upload failed:",result.status,result.detail);
      alert(`Logo upload failed (HTTP ${result.status}). This usually means org_settings_migration.sql hasn't been run yet. See the browser console for the exact error.`);
    }
    setUploading(false);
    if(inputRef.current)inputRef.current.value="";
  }
  return(
    <div style={{display:"flex",alignItems:"center",gap:12}}>
      <div style={{width:60,height:60,borderRadius:10,background:"var(--c-bg0)",border:`2px solid ${ac}55`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
        {logoUrl?<img src={logoUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:26}}>⚽</span>}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        <input ref={inputRef} type="file" accept="image/*" onChange={onFileChange} style={{display:"none"}}/>
        <button onClick={()=>inputRef.current?.click()} disabled={uploading} style={{padding:"7px 12px",borderRadius:6,border:`1px solid ${ac}`,background:ac+"22",color:ac,fontSize:11,fontWeight:600,cursor:"pointer"}}>{uploading?"Uploading…":"📁 Upload Logo"}</button>
      </div>
    </div>
  );
}

export function PlayerPhotoUploader({playerId,photoUrl,name,size,ac,token,canEdit,onUploaded}){
  const [uploading,setUploading]=useState(false);
  const inputRef=useRef(null);
  async function onFileChange(e){
    const file=e.target.files?.[0];
    if(!file)return;
    if(!file.type.startsWith("image/")){alert("Please choose an image file.");return;}
    if(file.size>5*1024*1024){alert("Please choose an image under 5MB.");return;}
    setUploading(true);
    const ext=(file.name.split(".").pop()||"jpg").toLowerCase();
    const path=`${playerId}/photo.${ext}`;
    const result=await api.uploadFile(token,"player-photos",path,file);
    if(result.ok){
      const url=`${SUPA_URL}/storage/v1/object/public/player-photos/${path}?t=${Date.now()}`;
      const res=await api.patch(token,"players",playerId,{photo_url:url});
      if(res&&res.length>0&&onUploaded)onUploaded(url);
      else { console.error("Photo uploaded but saving photo_url to the player record failed."); alert("Photo uploaded, but couldn't save it to the player's profile — please try again."); }
    } else {
      console.error("Photo upload failed:",result.status,result.detail);
      alert(`Photo upload failed (HTTP ${result.status}). This usually means either the player_photos_migration.sql hasn't been run yet, or its storage policies don't match your Supabase setup. See the browser console for the exact error message.`);
    }
    setUploading(false);
    if(inputRef.current)inputRef.current.value="";
  }
  return(
    <div style={{position:"relative",flexShrink:0}}>
      <PlayerAvatar url={photoUrl} name={name} size={size} ac={ac}/>
      {canEdit&&(
        <>
          <input ref={inputRef} type="file" accept="image/*" onChange={onFileChange} style={{display:"none"}}/>
          <button onClick={()=>inputRef.current?.click()} disabled={uploading} title="Change photo" style={{position:"absolute",bottom:-2,right:-2,width:size?Math.round(size*0.45):18,height:size?Math.round(size*0.45):18,borderRadius:"50%",border:"2px solid var(--c-surface)",background:ac,color:"#fff",fontSize:size?size*0.22:10,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>{uploading?"…":"📷"}</button>
        </>
      )}
    </div>
  );
}

export function AchievementBadges({rows,emptyText,size}){
  const [openKey,setOpenKey]=useState(null);
  const grouped={};
  (rows||[]).forEach(r=>{ grouped[r.achievement_key]=(grouped[r.achievement_key]||0)+1; });
  const keys=Object.keys(grouped);
  const big=size==="lg";
  if(keys.length===0)return <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>{emptyText||"No achievements yet."}</div>;
  return(
    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
      {keys.map(k=>{
        const info=achInfo(k);
        const count=grouped[k];
        const isOpen=openKey===k;
        return(
          <div key={k} onClick={()=>setOpenKey(isOpen?null:k)} title={info.desc} style={{display:"flex",flexDirection:"column",background:"var(--c-bg0)",border:"1px solid var(--c-border)",borderRadius:10,padding:big?"8px 12px":"6px 10px",cursor:"pointer",maxWidth:isOpen?200:undefined}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:big?20:16}}>{info.icon}</span>
              <span style={{fontSize:big?12:11,fontWeight:700}}>{info.name}</span>
              {count>1&&<span style={{fontSize:big?11:10,fontWeight:800,color:"#f59e0b"}}>×{count}</span>}
            </div>
            {isOpen&&<div style={{fontSize:10,color:"var(--c-muted)",marginTop:5,lineHeight:1.4}}>{info.desc}</div>}
          </div>
        );
      })}
    </div>
  );
}

// For horizontally-scrollable tables that pin a summary column (Total, Average,
// etc.) to one edge with position:sticky so it's always visible — this tracks
// whether any of the table's scrollable content is currently hidden behind
// that sticky column, so the caller can show a small "there's more, scroll"
// affordance only when it's actually true (not once permanently, and not only
// on first render — it re-checks on scroll/resize/content-size changes).
// Usage: const [scrollRef,hasHiddenContent]=useStickyEdgeOverflow("right");
// then spread {ref:scrollRef} onto the overflowX:auto container.
export function useStickyEdgeOverflow(edge="right"){
  const ref=useRef(null);
  const [hidden,setHidden]=useState(false);
  useEffect(()=>{
    const el=ref.current;
    if(!el)return;
    function check(){
      const overflow=el.scrollWidth-el.clientWidth;
      if(overflow<=4){ setHidden(false); return; }
      setHidden(edge==="right"?(overflow-el.scrollLeft>4):el.scrollLeft>4);
    }
    check();
    el.addEventListener("scroll",check,{passive:true});
    window.addEventListener("resize",check);
    const ro=typeof ResizeObserver!=="undefined"?new ResizeObserver(check):null;
    if(ro)ro.observe(el);
    return ()=>{ el.removeEventListener("scroll",check); window.removeEventListener("resize",check); if(ro)ro.disconnect(); };
  },[edge]);
  return [ref,hidden];
}

// Tracks which stat cards/rows a person has chosen to see on a stats page. Resets to
// "All" every time the page/component is freshly mounted (i.e. every page load) rather
// than being persisted — the ask was specifically "default to All when the page loads",
// not "remember my last choice".
export function useStatsVisibility(options){
  const [visible,setVisible]=useState(()=>new Set((options||[]).map(o=>o.id)));
  return [visible,setVisible];
}

// A small checkbox dropdown for choosing which stats are visible on a stats page.
// `options` is [{id,label}], `visible` is a Set of currently-visible ids, `setVisible`
// updates it. Includes an "All" checkbox that selects/clears everything at once.
export function StatsFilterMenu({options,visible,setVisible,AS,label="Show Stats"}){
  const [open,setOpen]=useState(false);
  if(!options||options.length===0)return null;
  const allSelected=options.every(o=>visible.has(o.id));
  function toggleAll(){ setVisible(allSelected?new Set():new Set(options.map(o=>o.id))); }
  function toggleOne(id){ setVisible(prev=>{ const next=new Set(prev); if(next.has(id))next.delete(id);else next.add(id); return next; }); }
  return(
    <div style={{position:"relative",display:"inline-block"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{padding:"6px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text2)",fontSize:11,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
        📊 {label} {allSelected?"(All)":visible.size===0?"(None)":`(${visible.size})`} <span style={{fontSize:9,opacity:0.7}}>▾</span>
      </button>
      {open&&(<>
        <div onClick={()=>setOpen(false)} style={{position:"fixed",inset:0,zIndex:150}}/>
        <div style={{position:"absolute",top:"100%",right:0,marginTop:6,background:AS.surfaceColor,border:`1px solid ${AS.borderColor}`,borderRadius:10,padding:8,minWidth:200,maxHeight:320,overflowY:"auto",zIndex:151,boxShadow:"0 10px 30px rgba(0,0,0,0.25)"}}>
          <label style={{display:"flex",alignItems:"center",gap:8,padding:"7px 6px",fontWeight:700,fontSize:12,cursor:"pointer",borderBottom:`1px solid ${AS.borderColor}`,marginBottom:4}}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll}/> All
          </label>
          {options.map(o=>(
            <label key={o.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 6px",fontSize:12,cursor:"pointer"}}>
              <input type="checkbox" checked={visible.has(o.id)} onChange={()=>toggleOne(o.id)}/> {o.label}
            </label>
          ))}
        </div>
      </>)}
    </div>
  );
}

export function PlayerDevelopmentSection({playerId,token,coachId,canEdit,ac,AS}){
  const [items,setItems]=useState(null); // null = loading
  const [pointTotals,setPointTotals]=useState({}); // {developmentId: summedDelta}
  const [adding,setAdding]=useState(null); // which category is currently showing its "add" form
  const [customText,setCustomText]=useState("");

  useEffect(()=>{ load(); },[playerId]);
  async function load(){
    setItems(null);
    const rows=await api.get(token,"player_development",`player_id=eq.${playerId}&order=created_at.asc`);
    setItems(rows||[]);
    if(rows&&rows.length>0){
      const ids=rows.map(r=>r.id).join(",");
      const pts=await api.get(token,"player_development_points",`development_id=in.(${ids})`);
      const totals={};
      (pts||[]).forEach(p=>{totals[p.development_id]=(totals[p.development_id]||0)+p.delta;});
      setPointTotals(totals);
    } else {
      setPointTotals({});
    }
  }
  async function addItem(category,text){
    const t=text.trim();
    if(!t)return;
    const created=await api.post(token,"player_development",[{player_id:playerId,category,text_content:t,created_by:coachId}]);
    if(created&&created[0])setItems(prev=>[...(prev||[]),created[0]]);
    setCustomText("");setAdding(null);
  }
  async function removeItem(id){
    await api.del(token,"player_development",id);
    setItems(prev=>(prev||[]).filter(i=>i.id!==id));
  }
  function toggleAdd(category){ setAdding(a=>a===category?null:category); setCustomText(""); }

  const groups={strength:(items||[]).filter(i=>i.category==="strength"),development:(items||[]).filter(i=>i.category==="development"),note:(items||[]).filter(i=>i.category==="note")};

  return(
    <div>
      <PlayerDevelopmentBlock label="💪 Strengths" category="strength" color="#22c55e" suggestions={STRENGTH_SUGGESTIONS} showScore={true} list={groups.strength} itemsLoaded={items!==null} isAdding={adding==="strength"} canEdit={canEdit} ac={ac} AS={AS} pointTotals={pointTotals} customText={customText} setCustomText={setCustomText} onToggleAdd={toggleAdd} onAddItem={addItem} onRemoveItem={removeItem}/>
      <PlayerDevelopmentBlock label="📈 Areas for Development" category="development" color="#f59e0b" suggestions={DEVELOPMENT_SUGGESTIONS} showScore={true} list={groups.development} itemsLoaded={items!==null} isAdding={adding==="development"} canEdit={canEdit} ac={ac} AS={AS} pointTotals={pointTotals} customText={customText} setCustomText={setCustomText} onToggleAdd={toggleAdd} onAddItem={addItem} onRemoveItem={removeItem}/>
      <PlayerDevelopmentBlock label="📝 Other Notes" category="note" color="var(--c-muted2)" suggestions={null} list={groups.note} itemsLoaded={items!==null} isAdding={adding==="note"} canEdit={canEdit} ac={ac} AS={AS} pointTotals={pointTotals} customText={customText} setCustomText={setCustomText} onToggleAdd={toggleAdd} onAddItem={addItem} onRemoveItem={removeItem}/>
    </div>
  );
}

// Hoisted out of PlayerDevelopmentSection (was previously a function defined
// inside that component's render body). Defining a component inside another
// component's render means React sees a brand-new component type on every
// parent re-render, forcing a full unmount/remount of the subtree — which
// broke the "Other Notes" input's focus on every keystroke (typing updated
// `customText` in the parent, which re-rendered and remounted this whole
// block, including the input, losing focus each time). Now a proper
// top-level component with everything passed in as explicit props.
function PlayerDevelopmentBlock({label,category,suggestions,color,showScore,list,itemsLoaded,isAdding,canEdit,ac,AS,pointTotals,customText,setCustomText,onToggleAdd,onAddItem,onRemoveItem}){
  return(
    <div style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
        <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</div>
        {canEdit&&<button onClick={()=>onToggleAdd(category)} style={{fontSize:10,fontWeight:700,color:ac,background:"none",border:"none",cursor:"pointer"}}>{isAdding?"Cancel":"+ Add"}</button>}
      </div>
      {!itemsLoaded?(
        <div style={{fontSize:11,color:"var(--c-muted2)"}}>Loading…</div>
      ):(list.length===0&&!isAdding)?(
        <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>Nothing added yet.</div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:isAdding?8:0}}>
          {list.map(i=>{
            const score=pointTotals[i.id]||0;
            return(
              <div key={i.id} style={{background:"var(--c-bg0)",borderRadius:7,padding:"7px 10px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0}}/>
                  <span style={{flex:1,fontSize:12}}>{i.text_content}</span>
                  {canEdit&&<button onClick={()=>onRemoveItem(i.id)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:13,padding:0}}>✕</button>}
                </div>
                {showScore&&(
                  score===0?(
                    <div style={{marginLeft:14,marginTop:5,fontSize:9,color:"var(--c-muted2)"}}>No points yet</div>
                  ):(
                    <div title={`${Math.abs(score)} point${Math.abs(score)===1?"":"s"} ${score>0?"gained":"lost"}`} style={{display:"flex",gap:3,flexWrap:"wrap",marginLeft:14,marginTop:5}}>
                      {Array.from({length:Math.abs(score)},(_,idx)=>idx+1).map(n=>(
                        <span key={n} style={{width:17,height:17,borderRadius:"50%",background:score>0?"#22c55e":"#ef4444",color:"#fff",fontSize:8,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{n}</span>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
      {isAdding&&(
        <div style={{background:"var(--c-bg0)",borderRadius:8,padding:10}}>
          {suggestions&&<div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:8}}>
            {suggestions.filter(s=>!list.some(i=>i.text_content===s)).map(s=>(
              <button key={s} onClick={()=>onAddItem(category,s)} style={{fontSize:10,padding:"4px 9px",borderRadius:12,border:`1px solid ${AS.borderColor}`,background:"var(--c-surface)",color:"var(--c-text2)",cursor:"pointer"}}>+ {s}</button>
            ))}
          </div>}
          <div style={{display:"flex",gap:6}}>
            <input value={customText} onChange={e=>setCustomText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")onAddItem(category,customText);}} placeholder="Write your own…" style={{flex:1,padding:"7px 9px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"var(--c-surface)",color:"var(--c-text)",fontSize:12,outline:"none"}}/>
            <button onClick={()=>onAddItem(category,customText)} style={{padding:"7px 14px",borderRadius:6,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── DevPointsPanel ───────────────────────────────────────────────────────────
// Lets either a coach or a player nudge a player's strengths/development areas
// by -1/0/+1 for one specific match. Used from the coach's match view and from
// the player's own match detail view (via the `source` prop).

export function DevPointsPanel({playerId,fixtureId,source,token,ac,AS}){
  const [items,setItems]=useState(null); // player_development rows (strengths + development only)
  const [points,setPoints]=useState({}); // {developmentId: {id, delta}} for this fixture+source

  useEffect(()=>{ load(); },[playerId,fixtureId]);
  async function load(){
    setItems(null);
    const devRows=await api.get(token,"player_development",`player_id=eq.${playerId}&category=in.(strength,development)&order=created_at.asc`);
    setItems(devRows||[]);
    if(devRows&&devRows.length>0){
      const ids=devRows.map(d=>d.id).join(",");
      const ptRows=await api.get(token,"player_development_points",`development_id=in.(${ids})&fixture_id=eq.${fixtureId}&source=eq.${source}`);
      const map={};
      (ptRows||[]).forEach(p=>{map[p.development_id]={id:p.id,delta:p.delta};});
      setPoints(map);
    } else {
      setPoints({});
    }
  }
  async function setDelta(developmentId,delta){
    const existing=points[developmentId];
    if(existing){
      const res=await api.patch(token,"player_development_points",existing.id,{delta,updated_at:new Date().toISOString()});
      if(res&&res.length>0)setPoints(prev=>({...prev,[developmentId]:{id:existing.id,delta}}));
    } else {
      const created=await api.post(token,"player_development_points",[{development_id:developmentId,fixture_id:fixtureId,source,delta}]);
      if(created&&created[0])setPoints(prev=>({...prev,[developmentId]:{id:created[0].id,delta}}));
    }
  }

  if(items===null)return <div style={{fontSize:11,color:"var(--c-muted2)"}}>Loading…</div>;
  if(items.length===0)return <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No strengths or development areas set for this player yet.</div>;
  return(
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {items.map(it=>{
        const cur=points[it.id]?.delta??0;
        return(
          <div key={it.id} style={{display:"flex",alignItems:"center",gap:8,background:"var(--c-surface)",borderRadius:8,padding:"8px 10px"}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:it.category==="strength"?"#22c55e":"#f59e0b",flexShrink:0}}/>
            <span style={{flex:1,fontSize:12}}>{it.text_content}</span>
            <div style={{display:"flex",gap:3}}>
              {[-1,0,1].map(d=>(
                <button key={d} onClick={()=>setDelta(it.id,d)} style={{width:26,height:24,borderRadius:5,border:"none",cursor:"pointer",fontSize:11,fontWeight:700,background:cur===d?ac:"var(--c-bg0)",color:cur===d?"#fff":"var(--c-muted)"}}>{d>0?"+1":d}</button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function FlagPicker({value,onChange,style}){
  const [open,setOpen]=useState(false);const [q,setQ]=useState("");const ref=useRef(null);
  useEffect(()=>{const h=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  const list=FLAGS.filter(f=>q===""||f.name.toLowerCase().includes(q.toLowerCase()));
  const sel=FLAGS.find(f=>f.flag===value);
  return(<div ref={ref} style={{position:"relative"}}>
    <button type="button" onClick={()=>{setOpen(o=>!o);setQ("");}} style={{...style,display:"flex",alignItems:"center",gap:8,cursor:"pointer",justifyContent:"space-between"}}>
      <span style={{display:"flex",alignItems:"center",gap:8}}>{value?<Flag value={value} size={20}/>:<span style={{fontSize:18}}>🏳</span>}<span style={{fontSize:12,color:sel?"var(--c-text)":"var(--c-muted)"}}>{sel?sel.name:"Select…"}</span></span>
      <span style={{color:"var(--c-muted)",fontSize:9}}>▼</span>
    </button>
    {open&&<div style={{position:"absolute",top:"calc(100% + 3px)",left:0,right:0,background:"var(--c-bg0)",border:"1px solid var(--c-muted2)",borderRadius:9,zIndex:300,boxShadow:"0 8px 32px rgba(0,0,0,0.6)",overflow:"hidden"}}>
      <div style={{padding:"7px 9px",borderBottom:"1px solid var(--c-border)"}}><input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" style={{width:"100%",padding:"6px 9px",borderRadius:6,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text)",fontSize:12,outline:"none",boxSizing:"border-box"}}/></div>
      <div style={{maxHeight:180,overflowY:"auto",padding:"5px 7px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:2}}>
        {list.map(f=><button key={f.flag+f.name} type="button" onClick={()=>{onChange(f.flag);setOpen(false);setQ("");}} style={{display:"flex",alignItems:"center",gap:7,padding:"6px 7px",borderRadius:6,border:"none",background:value===f.flag?"#1d4ed844":"transparent",cursor:"pointer",textAlign:"left"}} onMouseEnter={e=>{if(value!==f.flag)e.currentTarget.style.background="var(--c-surface)";}} onMouseLeave={e=>{if(value!==f.flag)e.currentTarget.style.background="transparent";}}>
          <span style={{flexShrink:0}}><Flag value={f.flag} size={18}/></span><span style={{fontSize:11,color:"var(--c-text3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
        </button>)}
      </div>
    </div>}
  </div>);
}

// ── Pitch ─────────────────────────────────────────────────────────────────────

export function TermsModal({onClose}){
  return(
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--c-surface)",borderRadius:14,border:"1px solid var(--c-border)",maxWidth:640,width:"100%",maxHeight:"85vh",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 20px",borderBottom:"1px solid var(--c-border)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontWeight:800,fontSize:16}}>Terms & Conditions and Privacy Policy</div>
            <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>Version {TERMS_VERSION} · Last updated {TERMS_LAST_UPDATED}</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"var(--c-muted)",padding:4}}>✕</button>
        </div>
        <div style={{padding:"16px 20px",overflowY:"auto"}}>
          {TERMS_SECTIONS.map(s=>(
            <div key={s.h} style={{marginBottom:16}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:5,color:"var(--c-text)"}}>{s.h}</div>
              <div style={{fontSize:12,color:"var(--c-text2)",lineHeight:1.65}}>{s.b}</div>
            </div>
          ))}
        </div>
        <div style={{padding:"12px 20px",borderTop:"1px solid var(--c-border)",textAlign:"right"}}>
          <button onClick={onClose} style={{padding:"9px 18px",borderRadius:7,border:"none",background:"#16a34a",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>Close</button>
        </div>
      </div>
    </div>
  );
}

export function FeaturesPanel({org,ac,AS,onToggle,compact}){
  // A grid of capped-width cards instead of one full-width row each — on a wide
  // desktop screen a single row was stretching the toggle switch far away from its
  // label with a lot of empty space in between; this keeps each row a sensible
  // width and lets several sit side by side instead.
  return(
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(240px, 320px))",gap:compact?8:10}}>
      {FEATURE_TOGGLES.map(t=>{
        const on=isFeatureOn(org,t);
        return(
          <div key={t.field} style={{background:AS.surfaceColor,border:`1px solid ${AS.borderColor}`,borderRadius:11,padding:compact?"10px 12px":14,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:compact?16:18,flexShrink:0}}>{t.icon}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:700,fontSize:compact?12:13}}>{t.label}</div>
              {!compact&&<div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>{t.desc}</div>}
            </div>
            <button onClick={()=>onToggle(t.field,!on)} title={on?`Turn off ${t.label}`:`Turn on ${t.label}`} style={{width:38,height:22,borderRadius:11,border:"none",cursor:"pointer",background:on?ac:"var(--c-border)",position:"relative",flexShrink:0}}>
              <span style={{position:"absolute",top:2,left:on?18:2,width:18,height:18,borderRadius:"50%",background:"#fff",transition:"left 0.15s"}}/>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── MatchTimeTable ────────────────────────────────────────────────────────────
// Shared between the per-team Stats page and the org-wide Overall Stats page —
// one row per player, one column per match that has saved Match Day playtime,
// plus a Total column. Horizontally scrollable since a full season's worth of
// matches won't fit in one screen width.
export function MatchTimeTable({players,fixtures,matchTimeByFixture,ac,AS,emptyText}){
  const playedWithTime=(fixtures||[])
    .filter(f=>!f.matches&&matchTimeByFixture&&matchTimeByFixture[f.id]&&Object.keys(matchTimeByFixture[f.id]).length>0)
    .sort((a,b)=>new Date(a.date||0)-new Date(b.date||0));
  const [scrollRef,hasHidden]=useStickyEdgeOverflow("right");
  if(playedWithTime.length===0||!players||players.length===0){
    return <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>{emptyText||"No Match Day data yet — playtime shows up here once a coach runs Match Day and finishes a match."}</div>;
  }
  // Scheduled length of each match (sum of its periods), used both as a
  // sub-header under each match column and to total up "minutes available"
  // across the whole set of matches — a baseline to compare actual playtime
  // against (e.g. "450 played / 810 available").
  const matchMinutes=f=>(f.match_day?.periods||[]).reduce((s,p)=>s+(parseInt(p.lengthMinutes)||0),0);
  const totalAvailableMinutes=playedWithTime.reduce((s,f)=>s+matchMinutes(f),0);
  const totalFor=pid=>playedWithTime.reduce((sum,f)=>sum+(matchTimeByFixture[f.id]?.[pid]||0),0);
  const sortedPlayers=[...players].sort((a,b)=>totalFor(b.id)-totalFor(a.id));
  const colWidth=64;
  const totalColWidth=80;
  // "Total Played"/"Total Available" stay pinned to the right edge (position:sticky)
  // instead of scrolling away with the match columns — they're the numbers people
  // actually come to this table for. hasHidden (from useStickyEdgeOverflow) is true
  // whenever a match column is currently scrolled underneath those pinned columns,
  // which is when the "‹‹ more" hint below is shown.
  return(
    <div ref={scrollRef} style={{overflowX:"auto",position:"relative"}}>
      <div style={{minWidth:180+playedWithTime.length*colWidth+totalColWidth*2,width:"100%"}}>
        <div style={{display:"grid",gridTemplateColumns:`minmax(150px,220px) repeat(${playedWithTime.length}, ${colWidth}px) ${totalColWidth}px ${totalColWidth}px`,gap:4,padding:"5px 6px",fontSize:9,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",background:AS.bgColor,borderRadius:"7px 7px 0 0"}}>
          <div>Player</div>
          {playedWithTime.map(f=>{
            const mins=matchMinutes(f);
            return(
              <div key={f.id} title={`vs ${f.opponent||"TBC"} · ${fmtDateShort(f.date)}${mins?` · ${mins} min match`:""}`} style={{textAlign:"center",overflow:"hidden"}}>
                <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.opponent?f.opponent.slice(0,8):"TBC"}</div>
                <div style={{fontSize:8,fontWeight:600,color:"var(--c-muted2)",textTransform:"none",marginTop:1}}>{mins>0?`${mins} min`:"—"}</div>
              </div>
            );
          })}
          <div style={{textAlign:"center",position:"sticky",right:totalColWidth,background:AS.bgColor,borderLeft:hasHidden?`1px solid ${AS.borderColor}`:"none"}}>{hasHidden&&<div style={{fontSize:9,color:ac,marginBottom:1}} title="More matches — scroll to see them">⋯</div>}Total<br/>Played</div>
          <div style={{textAlign:"center",position:"sticky",right:0,background:AS.bgColor}}>Total<br/>Available</div>
        </div>
        {sortedPlayers.map((p,i)=>(
          <div key={p.id} style={{display:"grid",gridTemplateColumns:`minmax(150px,220px) repeat(${playedWithTime.length}, ${colWidth}px) ${totalColWidth}px ${totalColWidth}px`,gap:4,padding:"7px 6px",alignItems:"center",background:AS.surfaceColor,borderBottom:i===sortedPlayers.length-1?`1px solid ${AS.borderColor}`:`1px solid ${AS.bgColor}`,borderLeft:`1px solid ${AS.borderColor}`,borderRight:`1px solid ${AS.borderColor}`}}>
            <span style={{fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><Flag value={p.nationality}/> {p.name}</span>
            {playedWithTime.map(f=>{
              const secs=matchTimeByFixture[f.id]?.[p.id];
              return <span key={f.id} style={{textAlign:"center",fontSize:11,color:secs?"var(--c-text)":"var(--c-muted2)"}}>{secs?fmtMins(secs):"—"}</span>;
            })}
            <span style={{textAlign:"center",fontSize:12,fontWeight:700,color:ac,position:"sticky",right:totalColWidth,background:AS.surfaceColor,borderLeft:hasHidden?`1px solid ${AS.borderColor}`:"none"}}>{fmtMins(totalFor(p.id))}</span>
            <span style={{textAlign:"center",fontSize:12,fontWeight:700,color:"var(--c-muted)",position:"sticky",right:0,background:AS.surfaceColor}}>{totalAvailableMinutes}'</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PlayerRatingsTable ────────────────────────────────────────────────────────
// One row per player, one column per match that has a coach rating, plus
// Coach Avg / Self Avg pinned to the right edge (position:sticky) so they're
// always visible while the match columns scroll underneath — same pattern as
// MatchTimeTable's Total columns, via useStickyEdgeOverflow above.
export function PlayerRatingsTable({players,fixtures,teamRatings,ac,AS,emptyText}){
  const avgOf=arr=>arr&&arr.length>0?Math.round((arr.reduce((a,b)=>a+b,0)/arr.length)*10)/10:null;
  const ratedFixtures=(fixtures||[])
    .filter(f=>!f.matches)
    .filter(f=>(players||[]).some(p=>teamRatings?.[p.id]?.byFixture?.[f.id]))
    .sort((a,b)=>new Date(a.date||0)-new Date(b.date||0));
  const [scrollRef,hasHidden]=useStickyEdgeOverflow("right");
  if(ratedFixtures.length===0||!players||players.length===0){
    return <div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>{emptyText||"No ratings yet — they'll show up here once a coach rates a match."}</div>;
  }
  const rated=players.map(p=>{
    const r=teamRatings?.[p.id]||{coach:[],player:[]};
    return {...p,coachAvg:avgOf(r.coach),selfAvg:avgOf(r.player)};
  }).sort((a,b)=>(b.coachAvg??-1)-(a.coachAvg??-1));
  const colWidth=56;
  const totalColWidth=76;
  return(
    <div ref={scrollRef} style={{overflowX:"auto",position:"relative"}}>
      <div style={{minWidth:150+ratedFixtures.length*colWidth+totalColWidth*2,width:"100%"}}>
        <div style={{display:"grid",gridTemplateColumns:`minmax(140px,200px) repeat(${ratedFixtures.length}, ${colWidth}px) ${totalColWidth}px ${totalColWidth}px`,gap:4,padding:"5px 6px",fontSize:8,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",background:AS.bgColor,borderRadius:"7px 7px 0 0"}}>
          <div>Player</div>
          {ratedFixtures.map(f=>(
            <div key={f.id} title={`vs ${f.opponent||"TBC"} · ${fmtDateShort(f.date)}`} style={{textAlign:"center",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.opponent?f.opponent.slice(0,7):"TBC"}</div>
          ))}
          <div title="Sorted highest to lowest" style={{textAlign:"center",position:"sticky",right:totalColWidth,background:AS.bgColor,borderLeft:hasHidden?`1px solid ${AS.borderColor}`:"none"}}>{hasHidden&&<div style={{fontSize:9,color:ac,marginBottom:1}} title="More matches — scroll to see them">⋯</div>}Coach<br/>Avg</div>
          <div style={{textAlign:"center",position:"sticky",right:0,background:AS.bgColor}}>Self<br/>Avg</div>
        </div>
        {rated.map((p,i)=>(
          <div key={p.id} style={{display:"grid",gridTemplateColumns:`minmax(140px,200px) repeat(${ratedFixtures.length}, ${colWidth}px) ${totalColWidth}px ${totalColWidth}px`,gap:4,padding:"7px 6px",alignItems:"center",background:AS.surfaceColor,borderBottom:i===rated.length-1?`1px solid ${AS.borderColor}`:`1px solid ${AS.bgColor}`,borderLeft:`1px solid ${AS.borderColor}`,borderRight:`1px solid ${AS.borderColor}`}}>
            <span style={{fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><Flag value={p.nationality}/> {p.name}</span>
            {ratedFixtures.map(f=>{
              const v=teamRatings?.[p.id]?.byFixture?.[f.id]?.coach;
              return <span key={f.id} style={{textAlign:"center",fontSize:11,color:v!=null?"var(--c-text)":"var(--c-muted2)"}}>{v!=null?v:"—"}</span>;
            })}
            <span style={{textAlign:"center",fontWeight:700,fontSize:12,color:p.coachAvg!==null?ac:"var(--c-muted2)",position:"sticky",right:totalColWidth,background:AS.surfaceColor,borderLeft:hasHidden?`1px solid ${AS.borderColor}`:"none"}}>{p.coachAvg!==null?`${p.coachAvg}/10`:"—"}</span>
            <span style={{textAlign:"center",fontSize:11,color:"var(--c-muted)",position:"sticky",right:0,background:AS.surfaceColor}}>{p.selfAvg!==null?`${p.selfAvg}/10`:"—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Match calendar ───────────────────────────────────────────────────────────
// A reusable monthly calendar for fixture lists — used as the "Calendar" toggle
// alongside the existing list view on the coach, parent, and player Matches pages.
// Callers normalise their own fixture shape into {id,date,title,subtitle,resultLabel,
// resultColor} items; clicking a fixture calls onSelectItem with that same item so
// each app can open its own existing match detail view/modal.
export function MatchCalendarView({items,ac,AS,onSelectItem}){
  const validItems=(items||[]).filter(it=>it.date);
  const [cursor,setCursor]=useState(()=>{
    const withDate=validItems.find(it=>it.date&&!it.played)||validItems[validItems.length-1];
    const base=withDate?new Date(withDate.date):new Date();
    return isNaN(base)?new Date():new Date(base.getFullYear(),base.getMonth(),1);
  });
  const [selectedDate,setSelectedDate]=useState(null);
  const detailsRef=useRef(null);
  // Scroll the day's fixture(s) into view the moment a day is picked — belt-and-braces
  // alongside shrinking the grid itself, so the result is never left off-screen.
  useEffect(()=>{
    if(selectedDate&&detailsRef.current){
      detailsRef.current.scrollIntoView({behavior:"smooth",block:"nearest"});
    }
  },[selectedDate]);
  const year=cursor.getFullYear(),month=cursor.getMonth();
  const firstOfMonth=new Date(year,month,1);
  const startWeekday=(firstOfMonth.getDay()+6)%7; // Monday=0
  const daysInMonth=new Date(year,month+1,0).getDate();
  const todayStr=today();
  const byDate={};
  validItems.forEach(it=>{(byDate[it.date]=byDate[it.date]||[]).push(it);});
  const cells=[];
  for(let i=0;i<startWeekday;i++)cells.push(null);
  for(let d=1;d<=daysInMonth;d++)cells.push(d);
  const monthLabel=cursor.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  const dayItems=selectedDate?(byDate[selectedDate]||[]):[];
  const navBtn={width:26,height:26,borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-text2)",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0};
  return(
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:7,maxWidth:340,marginLeft:"auto",marginRight:"auto"}}>
        <button onClick={()=>{setCursor(new Date(year,month-1,1));setSelectedDate(null);}} style={navBtn}>‹</button>
        <div style={{fontWeight:700,fontSize:12}}>{monthLabel}</div>
        <button onClick={()=>{setCursor(new Date(year,month+1,1));setSelectedDate(null);}} style={navBtn}>›</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2,maxWidth:340,marginLeft:"auto",marginRight:"auto"}}>
        {["M","T","W","T","F","S","S"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:8,fontWeight:700,color:"var(--c-muted)",padding:"1px 0"}}>{d}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,maxWidth:340,marginLeft:"auto",marginRight:"auto"}}>
        {cells.map((d,i)=>{
          if(d===null)return <div key={i}/>;
          const dateStr=`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const dayFixtures=byDate[dateStr]||[];
          const isToday=dateStr===todayStr;
          const isSel=dateStr===selectedDate;
          return(
            <button key={i} onClick={()=>dayFixtures.length>0&&setSelectedDate(isSel?null:dateStr)} style={{
              height:34,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,
              borderRadius:6,border:isToday?`1px solid ${ac}`:"1px solid transparent",
              background:isSel?ac:dayFixtures.length>0?ac+"18":"transparent",
              color:isSel?"#fff":"var(--c-text)",cursor:dayFixtures.length>0?"pointer":"default",
              fontSize:10,fontWeight:isToday?800:500,padding:0,
            }}>
              <span>{d}</span>
              {dayFixtures.length>0&&<span style={{display:"flex",gap:1}}>
                {dayFixtures.slice(0,3).map(f=><span key={f.id} style={{width:3,height:3,borderRadius:"50%",background:isSel?"#fff":(f.resultColor||ac)}}/>)}
              </span>}
            </button>
          );
        })}
      </div>
      {selectedDate&&(
        <div ref={detailsRef} style={{marginTop:10,display:"flex",flexDirection:"column",gap:6,scrollMarginTop:12,scrollMarginBottom:12}}>
          {dayItems.length===0?(
            <div style={{fontSize:11,color:"var(--c-muted2)",textAlign:"center",padding:12}}>No fixtures on this day.</div>
          ):dayItems.map(it=>(
            <div key={it.id} onClick={()=>onSelectItem&&onSelectItem(it)} style={{background:AS.bgColor,border:`1px solid ${AS.borderColor}`,borderRadius:9,padding:"10px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:9}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:12}}>{it.title}</div>
                {it.subtitle&&<div style={{fontSize:10,color:"var(--c-muted)",marginTop:1}}>{it.subtitle}</div>}
              </div>
              {it.resultLabel&&<div style={{fontSize:13,fontWeight:900,color:it.resultColor||ac}}>{it.resultLabel}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Payments ─────────────────────────────────────────────────────────────────

